import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AiCaptainClient,
  AiCaptainSendPayload,
  AiCaptainStreamMessage,
  getAiCaptainWsUrl,
} from '../services/aiCaptainClient';
import cartService from '../services/cartService';
import { notifyCartUpdated } from '../utils/cartEvents';
import {
  type AiCaptainMenuItem,
  extractMenuIntroText,
  normalizeAiCaptainMenuItem,
} from '../utils/aiCaptainMenuItems';
import {
  type AiCaptainPlacedOrder,
  type AiCaptainTableItem,
  type AiCaptainTableStatus,
  normalizeAiCaptainTable,
  normalizeAiCaptainTableStatus,
} from '../utils/aiCaptainTables';
import {
  type AiCaptainChoicePrompt,
  normalizeAiCaptainChoicePrompt,
} from '../utils/aiCaptainChoices';
import {
  type AiCaptainDealItem,
  normalizeAiCaptainDeal,
} from '../utils/aiCaptainDeals';
import { formatCaptainErrorMessage } from '../utils/formatCaptainErrorMessage';
import { resolveAiCaptainPageContext } from '../utils/aiCaptainPageContext';
import {
  hasAiCaptainPageVisited,
  markAiCaptainPageVisited,
} from '../utils/captainSessionMemory';
import { CAPTAIN_TABLE_APPROVED_EVENT, CAPTAIN_TABLE_REQUESTED_EVENT } from '../utils/captainPanelEvents';

function resolveCaptainSpeakText(
  msg: AiCaptainStreamMessage,
  displayText: string,
  menuItemCount = 0
): string {
  if (msg.speakText?.trim()) return msg.speakText.trim();
  if (menuItemCount > 0) {
    const intro = extractMenuIntroText(displayText, menuItemCount);
    if (intro) return intro;
  }
  return displayText.trim();
}

function formatCartSummary(summary?: Record<string, unknown>): string {
  if (!summary) return 'Please review your cart and confirm to place the order.';
  const total = summary.total;
  const itemCount = summary.itemCount;
  if (itemCount != null && Number(itemCount) <= 0) {
    return '';
  }
  const lines = summary.lines as Array<Record<string, unknown>> | undefined;
  const lineText =
    lines && lines.length > 0
      ? lines
          .slice(0, 5)
          .map((line) => `${line.quantity ?? 1}× ${line.name ?? 'Item'}`)
          .join(', ')
      : '';
  const parts = ['Ready to place your order?'];
  if (lineText) parts.push(lineText);
  if (itemCount != null) parts.push(`${itemCount} item(s)`);
  if (total != null) parts.push(`Total ₹${Math.round(Number(total))}`);
  parts.push('Reply yes to confirm.');
  return parts.join(' · ');
}

function formatOrderPlaced(result?: Record<string, unknown>): string {
  const code = result?.orderCode ? String(result.orderCode) : result?.orderId ? `#${result.orderId}` : '';
  const prep = result?.estimatedPrepMinutes;
  if (code && prep != null) {
    return `Order ${code} placed! Estimated prep time: ${prep} minutes.`;
  }
  if (code) return `Order ${code} placed successfully!`;
  return 'Your order has been placed.';
}

export interface AiChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  streaming?: boolean;
  menuItems?: AiCaptainMenuItem[];
  tableItems?: AiCaptainTableItem[];
  dealItems?: AiCaptainDealItem[];
  tableStatus?: AiCaptainTableStatus;
  placedOrder?: AiCaptainPlacedOrder;
  choicePrompt?: AiCaptainChoicePrompt;
  choiceAnswered?: boolean;
  hasMore?: boolean;
}

export interface UseAiCaptainSessionOptions {
  enabled: boolean;
  appName: string | null;
  restaurantId: string;
  userId: number | null;
  tableId?: string | null;
  orderId?: number | null;
  pathname: string;
  onSpeak?: (text: string) => void;
  /** Proactive page-event replies (item detail, cart) — show in popover, not only chat */
  onPageNudge?: (text: string, speakText?: string, pageKey?: string) => void;
}

export default function useAiCaptainSession(options: UseAiCaptainSessionOptions) {
  const {
    enabled,
    appName,
    restaurantId,
    userId,
    tableId,
    orderId,
    pathname,
    onSpeak,
    onPageNudge,
  } = options;

  const [connected, setConnected] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const [chatMessages, setChatMessages] = useState<AiChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [streamingMenuItems, setStreamingMenuItems] = useState<AiCaptainMenuItem[]>([]);
  const [streamingTableItems, setStreamingTableItems] = useState<AiCaptainTableItem[]>([]);
  const [streamingDealItems, setStreamingDealItems] = useState<AiCaptainDealItem[]>([]);
  const [chatEnabled, setChatEnabled] = useState(false);
  const clientRef = useRef<AiCaptainClient | null>(null);
  const connectedRef = useRef(false);
  const lastPageRef = useRef<string | null>(null);
  const streamBufferRef = useRef('');
  const pendingMenuItemsRef = useRef<AiCaptainMenuItem[]>([]);
  const pendingHasMoreRef = useRef<boolean>(false);
  const pendingTableItemsRef = useRef<AiCaptainTableItem[]>([]);
  const pendingDealItemsRef = useRef<AiCaptainDealItem[]>([]);
  const onSpeakRef = useRef(onSpeak);
  const onPageNudgeRef = useRef(onPageNudge);
  const pageEventTurnRef = useRef(false);
  const pageEventDeliveredRef = useRef(false);
  const turnSpokeRef = useRef(false);
  const assistantCommittedThisTurnRef = useRef(false);

  const contextRef = useRef({
    appName,
    restaurantId,
    userId,
    tableId,
    orderId,
    pathname,
  });

  useEffect(() => {
    onSpeakRef.current = onSpeak;
    onPageNudgeRef.current = onPageNudge;
    contextRef.current = { appName, restaurantId, userId, tableId, orderId, pathname };
  }, [onSpeak, onPageNudge, appName, restaurantId, userId, tableId, orderId, pathname]);

  useEffect(() => {
    const onTableRequested = (event: Event) => {
      const detail = (event as CustomEvent<{ tableNumber: number; allotted: boolean }>).detail;
      const tableNumber = detail?.tableNumber;
      if (!tableNumber || tableNumber <= 0) return;
      const text = detail.allotted
        ? `Table ${tableNumber} is confirmed.`
        : `Table ${tableNumber} request sent — waiting for staff approval.`;
      setChatMessages((prev) => [
        ...prev,
        { id: `table-ui-${Date.now()}`, role: 'assistant', text },
      ]);
      onSpeakRef.current?.(text);
    };
    const onTableApproved = (event: Event) => {
      const detail = (event as CustomEvent<{ tableNumber: number }>).detail;
      const tableNumber = detail?.tableNumber;
      const text =
        tableNumber && tableNumber > 0
          ? `Table ${tableNumber} is ready! You can confirm your order whenever you're ready.`
          : 'Your table is ready! You can confirm your order whenever you\'re ready.';
      setChatMessages((prev) => [
        ...prev,
        { id: `table-ready-${Date.now()}`, role: 'assistant', text },
      ]);
      onSpeakRef.current?.(text);
    };
    window.addEventListener(CAPTAIN_TABLE_REQUESTED_EVENT, onTableRequested);
    window.addEventListener(CAPTAIN_TABLE_APPROVED_EVENT, onTableApproved);
    return () => {
      window.removeEventListener(CAPTAIN_TABLE_REQUESTED_EVENT, onTableRequested);
      window.removeEventListener(CAPTAIN_TABLE_APPROVED_EVENT, onTableApproved);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !appName || !userId) {
      clientRef.current?.disconnect();
      clientRef.current = null;
      connectedRef.current = false;
      setConnected(false);
      setConnectionFailed(false);
      setChatEnabled(false);
      return;
    }

    setConnectionFailed(false);
    const failTimer = window.setTimeout(() => {
      if (!connectedRef.current) {
        setConnectionFailed(true);
      }
    }, 8000);

    const client = new AiCaptainClient({
      wsUrl: getAiCaptainWsUrl(),
      onMessage: (msg: AiCaptainStreamMessage) => {
        const resolvePageEventKey = (): string | undefined => {
          const pageCtx = resolveAiCaptainPageContext(contextRef.current.pathname);
          if (!pageCtx) return undefined;
          return `${pageCtx.page}:${JSON.stringify(pageCtx.context)}`;
        };

        const deliverPageEventNudge = (text: string, speakText?: string): void => {
          const trimmed = text.trim();
          if (!trimmed || pageEventDeliveredRef.current) return;
          onPageNudgeRef.current?.(trimmed, speakText ?? trimmed, resolvePageEventKey());
          pageEventTurnRef.current = false;
          pageEventDeliveredRef.current = true;
        };

        const maybeSpeak = (text?: string): void => {
          const trimmed = text?.trim();
          if (!trimmed || turnSpokeRef.current) return;
          turnSpokeRef.current = true;
          onSpeakRef.current?.(trimmed);
        };

        const takePendingMenuItems = (): AiCaptainMenuItem[] => {
          const pending = pendingMenuItemsRef.current;
          pendingMenuItemsRef.current = [];
          setStreamingMenuItems([]);
          return pending;
        };
        const takePendingTableItems = (): AiCaptainTableItem[] => {
          const pending = pendingTableItemsRef.current;
          pendingTableItemsRef.current = [];
          setStreamingTableItems([]);
          return pending;
        };
        const takePendingDealItems = (): AiCaptainDealItem[] => {
          const pending = pendingDealItemsRef.current;
          pendingDealItemsRef.current = [];
          setStreamingDealItems([]);
          return pending;
        };

        const clearStreamPrefix = (): void => {
          streamBufferRef.current = '';
          setStreamingText('');
        };

        const resolveDoneDisplayText = (buffered: string, spoken?: string): string => {
          const trimmedBuffer = buffered.trim();
          const trimmedSpeak = spoken?.trim() ?? '';
          if (!trimmedSpeak) return trimmedBuffer;
          if (!trimmedBuffer) return trimmedSpeak;
          return trimmedSpeak.length > trimmedBuffer.length ? trimmedSpeak : trimmedBuffer;
        };

        switch (msg.type) {
          case 'token':
            streamBufferRef.current += msg.delta ?? '';
            setStreamingText(streamBufferRef.current);
            break;
          case 'suggestions': {
            clearStreamPrefix();
            const items = (msg.items ?? [])
              .map(normalizeAiCaptainMenuItem)
              .filter((item): item is AiCaptainMenuItem => item != null);
            if (items.length === 0) break;
            pendingMenuItemsRef.current = items;
            pendingHasMoreRef.current = msg.hasMore === true;
            setStreamingMenuItems(items);
            break;
          }
          case 'item_popover': {
            clearStreamPrefix();
            if (!msg.item) break;
            const item = normalizeAiCaptainMenuItem(msg.item);
            if (!item) break;
            pendingMenuItemsRef.current = [item];
            setStreamingMenuItems([item]);
            break;
          }
          case 'table_suggestions': {
            clearStreamPrefix();
            const tables = (msg.tables ?? []).map(normalizeAiCaptainTable);
            if (tables.length === 0) break;
            pendingTableItemsRef.current = tables;
            setStreamingTableItems(tables);
            break;
          }
          case 'deal_suggestions': {
            clearStreamPrefix();
            const deals = (msg.deals ?? [])
              .map((raw) => normalizeAiCaptainDeal(raw))
              .filter((d): d is AiCaptainDealItem => d != null);
            if (deals.length === 0) break;
            pendingDealItemsRef.current = deals;
            setStreamingDealItems(deals);
            break;
          }
          case 'choice_prompt': {
            if (!msg.choice) break;
            const choice = normalizeAiCaptainChoicePrompt(msg.choice);
            if (!choice) break;
            if (pageEventTurnRef.current && !pageEventDeliveredRef.current) {
              deliverPageEventNudge(choice.prompt, choice.speakText || choice.prompt);
            } else {
              maybeSpeak(choice.speakText || choice.prompt);
            }
            assistantCommittedThisTurnRef.current = true;
            setChatMessages((prev) => [
              ...prev,
              {
                id: `choice-${choice.promptId}`,
                role: 'assistant',
                text: choice.prompt,
                choicePrompt: choice,
              },
            ]);
            break;
          }
          case 'assistant_message': {
            if (!msg.text && !msg.choice) break;
            const menuItems = takePendingMenuItems();
            const hasMore = pendingHasMoreRef.current;
            pendingHasMoreRef.current = false;
            const tableItems = takePendingTableItems();
            const dealItems = takePendingDealItems();
            const choice = msg.choice ? normalizeAiCaptainChoicePrompt(msg.choice) : null;
            const displayText = msg.text?.trim() || choice?.prompt || '';
            const speakText = displayText
              ? resolveCaptainSpeakText(msg, displayText, menuItems.length)
              : choice?.speakText;
            if (pageEventTurnRef.current && displayText && !pageEventDeliveredRef.current) {
              const nudgeText =
                menuItems.length > 0 && msg.speakText?.trim()
                  ? msg.speakText.trim()
                  : displayText;
              deliverPageEventNudge(nudgeText, speakText ?? nudgeText);
            } else if (displayText) {
              maybeSpeak(resolveCaptainSpeakText(msg, displayText, menuItems.length));
            } else if (choice?.speakText) {
              maybeSpeak(choice.speakText);
            }
            assistantCommittedThisTurnRef.current = true;
            setChatMessages((prev) => [
              ...prev,
              {
                id: `ai-${Date.now()}`,
                role: 'assistant',
                text: displayText,
                menuItems: menuItems.length > 0 ? menuItems : undefined,
                tableItems: tableItems.length > 0 ? tableItems : undefined,
                dealItems: dealItems.length > 0 ? dealItems : undefined,
                choicePrompt: choice ?? undefined,
                hasMore: menuItems.length > 0 ? hasMore : undefined,
              },
            ]);
            streamBufferRef.current = '';
            setStreamingText('');
            break;
          }
          case 'table_status': {
            if (!msg.table) break;
            const status = normalizeAiCaptainTableStatus(msg.table);
            const spaceLabel = status.displayLabel
              || `${status.spaceNoun || (status.spaceType === 'ROOM' ? 'Room' : 'Table')} ${status.tableNumber}`;
            const statusText = status.allotted
              ? `${spaceLabel} is confirmed.`
              : `${spaceLabel} request sent — waiting for staff approval.`;
            setChatMessages((prev) => [
              ...prev,
              {
                id: `table-status-${Date.now()}`,
                role: 'assistant',
                text: statusText,
                tableStatus: status,
              },
            ]);
            break;
          }
          case 'order_placed': {
            if (!msg.order) break;
            const placed: AiCaptainPlacedOrder = {
              orderId: msg.order.orderId != null ? Number(msg.order.orderId) : undefined,
              orderCode: msg.order.orderCode ? String(msg.order.orderCode) : undefined,
              orderStatus: msg.order.orderStatus ? String(msg.order.orderStatus) : undefined,
              estimatedPrepMinutes:
                msg.order.estimatedPrepMinutes != null
                  ? Number(msg.order.estimatedPrepMinutes)
                  : undefined,
              orderType: msg.order.orderType != null ? Number(msg.order.orderType) : undefined,
              message: msg.order.message ? String(msg.order.message) : undefined,
            };
            setChatMessages((prev) => [
              ...prev,
              {
                id: `placed-${Date.now()}`,
                role: 'assistant',
                text: placed.message ?? 'Your order has been placed.',
                placedOrder: placed,
              },
            ]);
            break;
          }
          case 'action_result': {
            if (msg.ok) {
              const ctx = contextRef.current;
              const cart = msg.cart;
              const itemCount =
                cart && cart.itemCount != null ? Number(cart.itemCount) : undefined;
              if (ctx.appName) {
                if (typeof itemCount === 'number' && Number.isFinite(itemCount)) {
                  notifyCartUpdated(ctx.appName, itemCount);
                } else {
                  notifyCartUpdated(ctx.appName);
                }
              }
              let text = '';
              if (msg.action === 'add_to_cart') {
                text = 'Added to your cart.';
              } else if (msg.action === 'remove_from_cart') {
                text = 'Removed from your cart.';
              } else if (msg.action === 'update_cart_qty') {
                text = 'Cart updated.';
              }
              if (text) {
                setChatMessages((prev) => [
                  ...prev,
                  {
                    id: `cart-${Date.now()}`,
                    role: 'system',
                    text,
                  },
                ]);
              }
            }
            break;
          }
          case 'confirm_required':
            if (msg.action === 'place_order') {
              const confirmText = formatCartSummary(msg.summary);
              if (!confirmText) break;
              setChatMessages((prev) => [
                ...prev,
                {
                  id: `confirm-${Date.now()}`,
                  role: 'assistant',
                  text: confirmText,
                },
              ]);
              onSpeakRef.current?.(confirmText);
            }
            break;
          case 'error':
            takePendingMenuItems();
            setChatMessages((prev) => [
              ...prev,
              {
                id: `err-${Date.now()}`,
                role: 'system',
                text: formatCaptainErrorMessage(msg.message),
              },
            ]);
            break;
          case 'done':
            if (
              !assistantCommittedThisTurnRef.current &&
              (streamBufferRef.current || msg.speakText?.trim())
            ) {
              const buffered = streamBufferRef.current;
              const menuItems = takePendingMenuItems();
              const hasMore = pendingHasMoreRef.current;
              pendingHasMoreRef.current = false;
              const tableItems = takePendingTableItems();
              const dealItems = takePendingDealItems();
              const displayText = resolveDoneDisplayText(buffered, msg.speakText);
              const speakText = displayText
                ? resolveCaptainSpeakText(msg, displayText, menuItems.length)
                : msg.speakText?.trim();
              if (pageEventTurnRef.current && !pageEventDeliveredRef.current) {
                const nudgeText = msg.speakText?.trim() || displayText;
                if (nudgeText) {
                  deliverPageEventNudge(nudgeText, speakText || nudgeText);
                }
              } else if (!pageEventDeliveredRef.current) {
                maybeSpeak(speakText);
              }
              if (displayText || menuItems.length > 0 || tableItems.length > 0 || dealItems.length > 0) {
                setChatMessages((prev) => [
                  ...prev,
                  {
                    id: `ai-${Date.now()}`,
                    role: 'assistant',
                    text: displayText,
                    menuItems: menuItems.length > 0 ? menuItems : undefined,
                    tableItems: tableItems.length > 0 ? tableItems : undefined,
                    dealItems: dealItems.length > 0 ? dealItems : undefined,
                    hasMore: menuItems.length > 0 ? hasMore : undefined,
                  },
                ]);
              }
              clearStreamPrefix();
            } else if (
              !assistantCommittedThisTurnRef.current &&
              (pendingMenuItemsRef.current.length > 0 ||
              pendingTableItemsRef.current.length > 0 ||
              pendingDealItemsRef.current.length > 0)
            ) {
              const menuItems = takePendingMenuItems();
              const tableItems = takePendingTableItems();
              const dealItems = takePendingDealItems();
              if (
                pageEventTurnRef.current &&
                msg.speakText?.trim() &&
                !pageEventDeliveredRef.current
              ) {
                deliverPageEventNudge(msg.speakText.trim(), msg.speakText.trim());
              } else if (!pageEventDeliveredRef.current && msg.speakText?.trim()) {
                maybeSpeak(msg.speakText.trim());
              } else if (menuItems.length > 0 && msg.speakText?.trim()) {
                maybeSpeak(msg.speakText.trim());
              }
              setChatMessages((prev) => [
                ...prev,
                {
                  id: `cards-${Date.now()}`,
                  role: 'assistant',
                  text: '',
                  menuItems: menuItems.length > 0 ? menuItems : undefined,
                  tableItems: tableItems.length > 0 ? tableItems : undefined,
                  dealItems: dealItems.length > 0 ? dealItems : undefined,
                },
              ]);
            } else {
              clearStreamPrefix();
            }
            pageEventTurnRef.current = false;
            turnSpokeRef.current = false;
            assistantCommittedThisTurnRef.current = false;
            break;
          default:
            break;
        }
      },
      onConnectionChange: (isConnected) => {
        connectedRef.current = isConnected;
        setConnected(isConnected);
        if (isConnected) {
          setConnectionFailed(false);
        }
      },
    });
    client.connect();
    clientRef.current = client;
    setChatEnabled(true);
    lastPageRef.current = null;

    return () => {
      window.clearTimeout(failTimer);
      client.disconnect();
      clientRef.current = null;
      lastPageRef.current = null;
    };
  }, [enabled, appName, userId]);

  const buildPayload = useCallback(
    (type: 'user_message' | 'page_event', text?: string): AiCaptainSendPayload | null => {
      const ctx = contextRef.current;
      if (!ctx.appName || !ctx.userId) return null;
      const pageCtx = resolveAiCaptainPageContext(ctx.pathname);
      if (!pageCtx) return null;
      return {
        type,
        restaurantId: ctx.restaurantId,
        appName: ctx.appName,
        customerId: String(ctx.userId),
        userId: ctx.userId,
        tableId: ctx.tableId ?? null,
        orderId: ctx.orderId ?? null,
        page: pageCtx.page,
        context: pageCtx.context,
        text,
      };
    },
    []
  );

  const sendUserMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const payload = buildPayload('user_message', trimmed);
      if (!payload) return;
      setChatMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: 'user', text: trimmed },
      ]);
      pageEventTurnRef.current = false;
      pageEventDeliveredRef.current = false;
      turnSpokeRef.current = false;
      assistantCommittedThisTurnRef.current = false;
      clientRef.current?.send(payload);
    },
    [buildPayload]
  );

  const submitChoiceSelection = useCallback(
    (messageId: string, values: string[]) => {
      const text = values.map((v) => v.trim()).filter(Boolean).join(', ');
      if (!text) return;
      setChatMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, choiceAnswered: true } : m))
      );
      sendUserMessage(text);
    },
    [sendUserMessage]
  );

  const addMenuItemToCart = useCallback(
    async (item: AiCaptainMenuItem, orderType = 1) => {
      const ctx = contextRef.current;
      if (!ctx.appName) {
        return { ok: false as const, message: 'Restaurant not ready' };
      }
      try {
        const response = await cartService.addToCart(ctx.appName, item.id, 1, orderType);
        if (response.success) {
          setChatMessages((prev) => [
            ...prev,
            {
              id: `cart-btn-${Date.now()}`,
              role: 'system',
              text: `${item.name} added to your cart.`,
            },
          ]);
          return { ok: true as const };
        }
        return {
          ok: false as const,
          message: response.message ?? 'Could not add to cart',
        };
      } catch (err: unknown) {
        const apiMessage =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        return {
          ok: false as const,
          message: apiMessage || 'Could not add to cart. Try again.',
        };
      }
    },
    []
  );

  // Send page events only after WebSocket is connected — avoids connect/reconnect storms.
  useEffect(() => {
    if (!enabled || !connected || !clientRef.current) return;

    const pageCtx = resolveAiCaptainPageContext(pathname);
    if (!pageCtx) return;

    const pageKey = `${pageCtx.page}:${JSON.stringify(pageCtx.context)}`;
    if (lastPageRef.current === pageKey) return;
    if (hasAiCaptainPageVisited(pageKey)) {
      lastPageRef.current = pageKey;
      return;
    }
    lastPageRef.current = pageKey;
    markAiCaptainPageVisited(pageKey);

    const payload = buildPayload('page_event');
    if (payload) {
      pageEventTurnRef.current = true;
      pageEventDeliveredRef.current = false;
      turnSpokeRef.current = false;
      assistantCommittedThisTurnRef.current = false;
      clientRef.current.send(payload);
    }
  }, [pathname, enabled, connected, buildPayload]);

  return {
    connected,
    connectionFailed,
    chatEnabled,
    chatMessages,
    streamingText,
    streamingMenuItems,
    streamingTableItems,
    streamingDealItems,
    sendUserMessage,
    submitChoiceSelection,
    addMenuItemToCart,
  };
}
